import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { LoginCredentials } from '../../types/auth';
import { useTranslation } from 'react-i18next';
import './Login.css';

interface LoginProps {
  onLoginSuccess?: () => void;
}

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const { login, isLoggingIn } = useAuth();
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    identifier: '',
    password: ''
  });
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!credentials.identifier.trim() || !credentials.password.trim()) {
      setError(t('pleaseEnterCredentials'));
      return;
    }

    const success = await login(credentials);
    
    if (success) {
      setError('');
      onLoginSuccess?.();
    } else {
      setError(t('invalidCredentials'));
    }
  };

  return (
    <div className="login-overlay">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">
            <img src="/DIGITU_logo.png" alt="Digitu Logo" className="logo-image" />
          </div>
          <h2>{t('signIn')}</h2>
          <p>{t('signInDescription')}</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="identifier">
              {t('emailOrUsername')}
            </label>
            <input
              type="text"
              id="identifier"
              name="identifier"
              value={credentials.identifier}
              onChange={handleInputChange}
              placeholder={t('enterEmailOrUsername')}
              className={error ? 'error' : ''}
              disabled={isLoggingIn}
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              {t('password')}
            </label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={credentials.password}
                onChange={handleInputChange}
                placeholder={t('enterPassword')}
                className={error ? 'error' : ''}
                disabled={isLoggingIn}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoggingIn}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={isLoggingIn}
          >
            {isLoggingIn ? t('signingIn') : t('signIn')}
          </button>
        </form>

        <div className="login-footer">
          <p>{t('needAccount')} <button 
              type="button" 
              className="contact-admin-link"
              onClick={() => {/* Add contact admin logic here */}}
            >
              {t('contactAdmin')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};